import { createSlice } from '@reduxjs/toolkit';
import { setLoadingRedux } from '../commonslice';
import {
  login as loginApi,
  logout as logoutApi,
  resendMfaLogin,
  verifyMfaLogin,
} from '../../utils/api/auth_apis';
import { STATUS } from '../../utils/api/sharedfunction';

// const logger = require('../../../logger');

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    loading: false,
    error: null,
    mfaPending: false,
    mfaToken: null,
    mfaEmailHint: null,
    consentModalRequired: false,
    consentModalNonce: 0,
  },
  reducers: {
    setUser(state, action) {
      state.user = action.payload.user;
      if (action.payload.user && !action.payload.user.needs_reconsent) {
        state.consentModalRequired = false;
      }
    },
    setConsentModalRequired(state, action) {
      const required = Boolean(action.payload);
      state.consentModalRequired = required;
      if (required) {
        state.consentModalNonce += 1;
      }
    },
    updateUserInfo(state, action) {
      const { username, email } = action.payload; 
      if (state.user) {
        if (username) state.user.username = username; // Update username if present
        if (email) state.user.user_email = email; // Update email if present
      }
    },
    setLogout(state) {
      state.user = null;
      state.loading = false;
      state.error = null;
      state.mfaPending = false;
      state.mfaToken = null;
      state.mfaEmailHint = null;
      state.consentModalRequired = false;
    },
    setLoading(state, action) {
      state.loading = action.payload ?? true;
    },
    setError(state, action) {
      state.loading = false;
      state.error = action.payload;
    },
    clearError(state) {
      state.error = null;
    },
    setMfaPending(state, action) {
      state.mfaPending = true;
      state.mfaToken = action.payload.mfaToken;
      state.mfaEmailHint = action.payload.emailHint;
    },
    clearMfaPending(state) {
      state.mfaPending = false;
      state.mfaToken = null;
      state.mfaEmailHint = null;
    },
  },
});

export const {
  setUser,
  setLogout,
  setLoading,
  setError,
  clearError,
  updateUserInfo,
  setConsentModalRequired,
  setMfaPending,
  clearMfaPending,
} = authSlice.actions;

export const login = (email, password) => async (dispatch) => {
  dispatch(clearError());
  dispatch(setLoadingRedux(true));
  dispatch(setLoading());
  try {
    const response = await loginApi({ email, password });
    if (response.status === STATUS.SUCCESS) {
      if (response.data?.mfaRequired) {
        dispatch(setMfaPending(response.data));
      } else {
        dispatch(clearMfaPending());
        dispatch(setUser(response.data));
      }
    } else {
      const errorMessage = `Login failed: ${response.error || 'Please check your credentials.'}`;
      dispatch(setError(errorMessage));
    }
  } catch (error) {
    const errorMessage = `Login failed: ${error?.error || error?.message || 'Please check your credentials.'}`;
    dispatch(setError(errorMessage));
  } finally {
    dispatch(setLoading(false));
    dispatch(setLoadingRedux(false));
  }
};

export const verifyMfa = (otp) => async (dispatch, getState) => {
  const { mfaToken } = getState().auth;
  if (!mfaToken) {
    const error = 'Your MFA session has expired. Please sign in again.';
    dispatch(setError(error));
    dispatch(clearMfaPending());
    return { status: STATUS.ERROR, error };
  }

  dispatch(clearError());
  dispatch(setLoadingRedux(true));
  dispatch(setLoading());
  try {
    const response = await verifyMfaLogin({ mfaToken, otp });
    if (response.status === STATUS.SUCCESS) {
      dispatch(setUser(response.data));
      dispatch(clearMfaPending());
    } else {
      const errorMessage = response.error || 'Unable to verify the code.';
      dispatch(setError(errorMessage));
      if (
        /restart login/i.test(errorMessage) ||
        /expired/i.test(errorMessage) ||
        /Invalid or expired MFA challenge/i.test(errorMessage)
      ) {
        dispatch(clearMfaPending());
      }
    }
    return response;
  } catch (error) {
    const errorMessage = error?.error || error?.message || 'Unable to verify the code.';
    dispatch(setError(errorMessage));
    return { status: STATUS.ERROR, error: errorMessage };
  } finally {
    dispatch(setLoading(false));
    dispatch(setLoadingRedux(false));
  }
};

export const resendMfa = () => async (dispatch, getState) => {
  const { mfaToken } = getState().auth;
  if (!mfaToken) {
    const error = 'Your MFA session has expired. Please sign in again.';
    dispatch(setError(error));
    dispatch(clearMfaPending());
    return { status: STATUS.ERROR, error };
  }

  dispatch(clearError());
  try {
    const response = await resendMfaLogin({ mfaToken });
    if (response.status !== STATUS.SUCCESS) {
      const errorMessage = response.error || 'Unable to resend the verification code.';
      dispatch(setError(errorMessage));
      if (
        /restart login/i.test(errorMessage) ||
        /expired/i.test(errorMessage) ||
        /Invalid or expired MFA challenge/i.test(errorMessage)
      ) {
        dispatch(clearMfaPending());
      }
    }
    return response;
  } catch (error) {
    const errorMessage = error?.error || error?.message || 'Unable to resend the verification code.';
    dispatch(setError(errorMessage));
    return { status: STATUS.ERROR, error: errorMessage };
  }
};

export const logout = () => async (dispatch) => {
  try {
    dispatch(setLoadingRedux(true));
    dispatch(setLoading());
    dispatch(setLogout());
    await logoutApi();
    return Promise.resolve();
  } catch (error) {
    console.error('Logout failed:', error);
  } finally {
    dispatch(setLoading(false));
    dispatch(setLoadingRedux(false));
    dispatch(setError(null));
  }
};

export default authSlice.reducer;